import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Permission } from '@kreart/shared';
import { useAuth } from '@/features/auth/useAuth';

type CatalogTab = {
  to:
    | '/cabinets'
    | '/panels'
    | '/countertops'
    | '/drawers'
    | '/elements'
    | '/lighting'
    | '/hardware'
    | '/admin/catalog/materials'
    | '/admin/catalog/edges'
    | '/admin/catalog/drawer-systems';
  key: string;
  matchPrefixes?: readonly string[];
  /** When set, the tab is shown only to users holding this permission. */
  permission?: Permission;
};

const TABS: readonly CatalogTab[] = [
  { to: '/cabinets', key: 'cabinets' },
  { to: '/panels', key: 'panels' },
  { to: '/countertops', key: 'countertops' },
  { to: '/drawers', key: 'drawers' },
  // Component-library sections — hidden from roles without CATALOG_COMPONENTS.
  {
    to: '/lighting',
    key: 'lighting',
    matchPrefixes: ['/admin/catalog/lighting'],
    permission: Permission.CATALOG_COMPONENTS,
  },
  {
    to: '/hardware',
    key: 'hardware',
    matchPrefixes: ['/admin/catalog/hardware'],
    permission: Permission.CATALOG_COMPONENTS,
  },
  { to: '/admin/catalog/materials', key: 'materials', permission: Permission.CATALOG_COMPONENTS },
  { to: '/admin/catalog/edges', key: 'edges', permission: Permission.CATALOG_COMPONENTS },
  {
    to: '/admin/catalog/drawer-systems',
    key: 'drawerSystems',
    permission: Permission.CATALOG_COMPONENTS,
  },
  { to: '/elements', key: 'elements' },
] as const;

export function CatalogTabs() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const visibleTabs = TABS.filter(
    (tab) => !tab.permission || (user?.permissions.includes(tab.permission) ?? false),
  );
  return (
    <div className="flex gap-2 overflow-x-auto border-b border-border [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      {visibleTabs.map((tab) => {
        const active =
          pathname === tab.to ||
          pathname.startsWith(tab.to + '/') ||
          tab.matchPrefixes?.some(
            (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
          );
        return (
          <Link
            key={tab.to}
            to={tab.to}
            search={{}}
            className={
              '-mb-px shrink-0 border-b-2 px-4 py-2 text-sm font-medium transition-colors ' +
              (active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground')
            }
          >
            {t(`catalog.tabs.${tab.key}`)}
          </Link>
        );
      })}
    </div>
  );
}
